import { createContext, ReactNode, useContext, useEffect } from "react";
import { createPortal } from "react-dom";

// The DOM node every page's own toolbar (FY select, search, Filters/Sync/
// Export) portals into, so it renders once inside PageHeader instead of
// being duplicated in each of the 11 return-type/category pages. Each page
// keeps its own toolbar JSX, state and handlers completely unchanged - only
// where that JSX physically renders moves.
export const ToolbarSlotContext = createContext<HTMLDivElement | null>(null);

export function ToolbarPortal({ children }: { children: ReactNode }) {
  const node = useContext(ToolbarSlotContext);
  if (!node) return null;
  return createPortal(children, node);
}

export interface SelectionInfo {
  count: number;
  onRemove?: () => void;
  removing?: boolean;
}

const defaultSelection: SelectionInfo = { count: 0 };

// Lets the "Remove from tracking" button living in PageHeader's main header
// row (rendered once, above whichever return-type page is active) know the
// live selection count/handler of that active page, without PageHeader's
// two render branches needing to share props directly.
export const SelectionContext = createContext<SelectionInfo>(defaultSelection);
export const SelectionSetterContext = createContext<(info: SelectionInfo) => void>(() => {});

// Called by every page's toolbar-row PageHeader call to publish its live
// selection state into the shared context above.
export function useSelectionSync(info: SelectionInfo) {
  const setSelection = useContext(SelectionSetterContext);
  useEffect(() => {
    setSelection(info);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [info.count, info.onRemove, info.removing, setSelection]);
  useEffect(() => {
    return () => setSelection(defaultSelection);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setSelection]);
}
